// Sprint 2026-Jun — VoltLedger integration Phase G: HR Compliance.
// RC rows are STANDALONE manual RCs (unhooked from the PrimeWork Projects
// master, 2026-Jul-14 user directive). Add RCs via the Add RC dialog.
// Compliance data (C3 / ID Cards / NOC
// + Apr–Mar monthly Applied/Approved/No-Work statuses) is stored in the
// `hr_compliance_ext` extension collection. Direct edits + audit trail.
import { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/ui-helpers";
import { BranchFilter } from "@/components/BranchFilter";
import { AttachmentsField } from "@/components/AttachmentsField";
import { useAuth } from "@/contexts/AuthContext";
import { useBranch } from "@/contexts/BranchContext";
import { inputCls, Field } from "./Projects";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import * as XLSX from "xlsx";
import {
  ClipboardList, Download, Edit3, History, Loader2, Paperclip, Pencil, Plus, Save, Search, Trash2, X,
} from "lucide-react";

const EMPTY_BLOCK = {
  c3_applied: false, c3_applied_date: "", c3_received_date: "",
  total_employee_in_c3: "", total_id_card_applied: "", total_employee_as_per_hrms: "",
  id_card_applied_status_update_date: "",
  total_id_card_approved: "", id_card_approved_status_update_date: "",
  noc_received: false, noc_received_date: "", attachments: [],
};

const EMPTY_MANUAL = {
  rc_no: "", rc_description: "", project_name: "", client_name: "",
  branch_id: "", rc_date: "", rc_validity_date: "", rc_amount: "", active: true,
};

const cellTone = (st) => {
  if (st?.no_work) return "bg-rose-50 border-rose-300 text-rose-700";
  if (st?.approval_status) return "bg-emerald-50 border-emerald-300 text-emerald-700";
  if (st?.compliance_applied) return "bg-amber-50 border-amber-300 text-amber-700";
  return "bg-slate-50 border-slate-200 text-slate-400";
};
const cellLabel = (st) => {
  if (st?.no_work) return "No Work";
  if (st?.approval_status) return "Approved";
  if (st?.compliance_applied) return "Applied";
  return "—";
};

export default function HRCompliance() {
  const { hasPerm } = useAuth();
  const { branches } = useBranch();
  const canEdit = hasPerm("hr_compliance", "edit");
  const canCreate = hasPerm("hr_compliance", "create") || hasPerm("hr_compliance", "edit");
  const canDelete = hasPerm("hr_compliance", "delete");
  const canExport = hasPerm("hr_compliance", "export") || hasPerm("hr_compliance", "view");

  const [opts, setOpts] = useState({ fy_list: [], current_fy: "", months: [] });
  const [fy, setFY] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("monthly");

  const [q, setQ] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);

  // Detail (monthly grid) dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRC, setDetailRC] = useState(null);
  const [monthForm, setMonthForm] = useState({});
  const [history, setHistory] = useState([]);
  const [savingMonth, setSavingMonth] = useState("");

  // C3/ID/NOC edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editRC, setEditRC] = useState(null);
  const [blockForm, setBlockForm] = useState(EMPTY_BLOCK);
  const [savingBlock, setSavingBlock] = useState(false);

  // Manual RC add/edit dialog (standalone — not linked to PrimeWork projects)
  const [manualOpen, setManualOpen] = useState(false);
  const [manualId, setManualId] = useState(null);
  const [manualForm, setManualForm] = useState(EMPTY_MANUAL);
  const [savingManual, setSavingManual] = useState(false);

  useEffect(() => {
    api.get("/hr-compliance/options").then(({ data }) => {
      setOpts(data);
      setFY(data.current_fy);
    }).catch((e) => toast.error(e.response?.data?.detail || "Failed to load options"));
  }, []);

  const load = async () => {
    if (!fy) return;
    setLoading(true);
    try {
      const params = { fy };
      if (branchFilter) params.branch_id = branchFilter;
      if (activeOnly) params.active_only = true;
      const { data } = await api.get("/hr-compliance/rcs", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load RCs");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [fy, branchFilter, activeOnly]);

  const projectOptions = useMemo(() => {
    const set = new Set();
    items.forEach((r) => { if (r.project_name) set.add(r.project_name); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    let rows = items;
    if (projectFilter) rows = rows.filter((r) => r.project_name === projectFilter);
    const needle = q.trim().toLowerCase();
    if (needle) {
      rows = rows.filter((r) =>
        `${r.rc_no} ${r.project_name} ${r.project_code} ${r.client_name} ${r.branch_name}`
          .toLowerCase().includes(needle));
    }
    return rows;
  }, [items, q, projectFilter]);

  const totals = useMemo(() => {
    let applied = 0, approved = 0, noWork = 0;
    filtered.forEach((r) => {
      Object.values(r.fy_months || {}).forEach((st) => {
        if (st.no_work) noWork++;
        else {
          if (st.compliance_applied) applied++;
          if (st.approval_status) approved++;
        }
      });
    });
    return { applied, approved, noWork };
  }, [filtered]);

  // === Add a new FY (client-side; persisted on first month save) ===
  const addNewFY = () => {
    const input = window.prompt(
      "Enter Financial Year in YYYY-YY format (e.g. 2028-29).\n\nIt persists permanently once any monthly status is saved against it.", "");
    if (!input) return;
    const v = String(input).trim();
    const m = v.match(/^(\d{4})-(\d{2})$/);
    if (!m) return toast.error("Invalid format. Use YYYY-YY (e.g. 2028-29).");
    if (parseInt(m[2], 10) !== (parseInt(m[1], 10) + 1) % 100)
      return toast.error(`Invalid range. After ${m[1]} the next year ends in ${String((parseInt(m[1], 10) + 1) % 100).padStart(2, "0")}.`);
    if ((opts.fy_list || []).includes(v)) { setFY(v); return toast.info(`${v} already exists — switched to it.`); }
    setOpts({ ...opts, fy_list: [...(opts.fy_list || []), v].sort().reverse() });
    setFY(v);
    toast.success(`FY ${v} added`);
  };

  // === Detail (monthly grid) ===
  const openDetail = (r) => {
    setDetailRC(r);
    setMonthForm(JSON.parse(JSON.stringify(r.fy_months || {})));
    setDetailOpen(true);
    setHistory([]);
    api.get(`/hr-compliance/rcs/${r.project_id}/${r.contract_id}/history`)
      .then(({ data }) => setHistory(data.history || [])).catch(() => {});
  };
  const updateMonth = (m, patch) => setMonthForm((prev) => ({ ...prev, [m]: { ...prev[m], ...patch } }));
  const saveMonth = async (m) => {
    if (!detailRC) return;
    setSavingMonth(m);
    try {
      const st = monthForm[m] || {};
      const noWork = !!st.no_work;
      const payload = {
        compliance_applied: noWork ? false : !!st.compliance_applied,
        compliance_applied_date: noWork ? null : (st.compliance_applied_date || null),
        compliance_id: noWork ? "" : (st.compliance_id || ""),
        approval_status: noWork ? false : !!st.approval_status,
        approval_date: noWork ? null : (st.approval_date || null),
        no_work: noWork,
        remarks: st.remarks || "",
      };
      const { data } = await api.put(
        `/hr-compliance/rcs/${detailRC.project_id}/${detailRC.contract_id}/months/${fy}/${m}`, payload);
      setMonthForm((prev) => ({ ...prev, [m]: data.status }));
      toast.success(`${m} FY ${fy} saved`);
      load();
      api.get(`/hr-compliance/rcs/${detailRC.project_id}/${detailRC.contract_id}/history`)
        .then(({ data: h }) => setHistory(h.history || [])).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSavingMonth(""); }
  };

  // === C3/ID/NOC block edit ===
  const openEdit = (r) => {
    const c = r.compliance || {};
    setEditRC(r);
    setBlockForm({
      ...EMPTY_BLOCK, ...c,
      c3_applied_date: c.c3_applied_date || "",
      c3_received_date: c.c3_received_date || "",
      id_card_applied_status_update_date: c.id_card_applied_status_update_date || "",
      id_card_approved_status_update_date: c.id_card_approved_status_update_date || "",
      noc_received_date: c.noc_received_date || "",
      total_employee_in_c3: c.total_employee_in_c3 ?? "",
      total_id_card_applied: c.total_id_card_applied ?? "",
      total_employee_as_per_hrms: c.total_employee_as_per_hrms ?? "",
      total_id_card_approved: c.total_id_card_approved ?? "",
      attachments: c.attachments || [],
      remarks: "",
    });
    setEditOpen(true);
  };
  const saveBlock = async () => {
    if (!editRC) return;
    setSavingBlock(true);
    try {
      const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
      const payload = {
        c3_applied: !!blockForm.c3_applied,
        c3_applied_date: blockForm.c3_applied ? (blockForm.c3_applied_date || null) : null,
        c3_received_date: blockForm.c3_received_date || null,
        total_employee_in_c3: num(blockForm.total_employee_in_c3),
        total_id_card_applied: num(blockForm.total_id_card_applied),
        total_employee_as_per_hrms: num(blockForm.total_employee_as_per_hrms),
        id_card_applied_status_update_date: blockForm.id_card_applied_status_update_date || null,
        total_id_card_approved: num(blockForm.total_id_card_approved),
        id_card_approved_status_update_date: blockForm.id_card_approved_status_update_date || null,
        noc_received: !!blockForm.noc_received,
        noc_received_date: blockForm.noc_received ? (blockForm.noc_received_date || null) : null,
        attachments: blockForm.attachments || [],
        remarks: blockForm.remarks || "",
      };
      await api.put(`/hr-compliance/rcs/${editRC.project_id}/${editRC.contract_id}`, payload);
      toast.success("Compliance details saved");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSavingBlock(false); }
  };

  // === Manual RC add / edit / delete ===
  const openManual = (r) => {
    if (r) {
      setManualId(r.contract_id);
      setManualForm({
        rc_no: r.rc_no || "", rc_description: r.rc_description || "",
        project_name: r.project_name || "", client_name: r.client_name || "",
        branch_id: r.branch_id || "", rc_date: r.rc_date || "",
        rc_validity_date: r.rc_validity_date || "", rc_amount: r.rc_amount ?? "",
        active: r.project_status === "Active",
      });
    } else {
      setManualId(null);
      setManualForm(EMPTY_MANUAL);
    }
    setManualOpen(true);
  };
  const saveManual = async () => {
    if (!manualForm.rc_no.trim()) return toast.error("RC Number is required");
    if (!manualForm.project_name.trim()) return toast.error("Project / Site name is required");
    setSavingManual(true);
    try {
      const payload = {
        ...manualForm,
        rc_no: manualForm.rc_no.trim(),
        project_name: manualForm.project_name.trim(),
        rc_amount: Number(manualForm.rc_amount || 0),
        rc_date: manualForm.rc_date || null,
        rc_validity_date: manualForm.rc_validity_date || null,
      };
      if (manualId) await api.put(`/hr-compliance/manual-rcs/${manualId}`, payload);
      else await api.post("/hr-compliance/manual-rcs", payload);
      toast.success(manualId ? "Manual RC updated" : "Manual RC added");
      setManualOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSavingManual(false); }
  };
  const deleteManual = async (r) => {
    if (!window.confirm(`Delete manual RC "${r.rc_no}"?\n\nThis cannot be undone. RCs with monthly compliance history cannot be deleted — mark them Inactive instead.`)) return;
    try {
      await api.delete(`/hr-compliance/manual-rcs/${r.contract_id}`);
      toast.success("Manual RC deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // === Excel export — both tabs as separate sheets ===
  const exportExcel = () => {
    if (!filtered.length) return toast.error("No data to export");
    const months = opts.months || [];
    const wb = XLSX.utils.book_new();

    const monthlyHeader = [
      "Sl No", "RC No (Contract)", "Project", "Project Code", "Client", "Branch", "Status",
      "RC Date", "RC Validity", "RC Amount",
      "C3 Applied", "C3 Applied Date", "C3 Received Date",
      "Employees in C3", "Employees per HRMS",
      "ID Cards Applied", "ID Card Applied Status Date",
      "ID Cards Approved", "ID Card Approved Status Date",
      "NOC Received", "NOC Received Date",
    ];
    months.forEach((m) => {
      monthlyHeader.push(`${m} - Status`, `${m} - Applied Date`, `${m} - Compliance ID`, `${m} - Approval Date`);
    });
    const monthlyRows = [monthlyHeader];
    filtered.forEach((r, idx) => {
      const c = r.compliance || {};
      const row = [
        idx + 1, r.rc_no, r.project_name, r.project_code, r.client_name, r.branch_name, r.project_status,
        r.rc_date || "", r.rc_validity_date || "", Number(r.rc_amount || 0),
        c.c3_applied ? "Yes" : "No", c.c3_applied_date || "", c.c3_received_date || "",
        c.total_employee_in_c3 ?? "", c.total_employee_as_per_hrms ?? "",
        c.total_id_card_applied ?? "", c.id_card_applied_status_update_date || "",
        c.total_id_card_approved ?? "", c.id_card_approved_status_update_date || "",
        c.noc_received ? "Yes" : "No", c.noc_received_date || "",
      ];
      months.forEach((m) => {
        const st = r.fy_months?.[m] || {};
        const label = st.no_work ? "No Work" : st.approval_status ? "Approved" : st.compliance_applied ? "Applied" : "Pending";
        row.push(label, st.compliance_applied_date || "", st.compliance_id || "", st.approval_date || "");
      });
      monthlyRows.push(row);
    });
    const ws1 = XLSX.utils.aoa_to_sheet(monthlyRows);
    XLSX.utils.book_append_sheet(wb, ws1, `Compliance FY ${fy}`);

    const mpHeader = ["Sl No", "RC No (Contract)", "Project", "Client", "Branch",
      "Employees per HRMS", "ID Cards Applied", "ID Cards Approved"];
    const mpRows = [mpHeader];
    let sHrms = 0, sApp = 0, sAppr = 0;
    filtered.forEach((r, idx) => {
      const c = r.compliance || {};
      sHrms += Number(c.total_employee_as_per_hrms || 0);
      sApp += Number(c.total_id_card_applied || 0);
      sAppr += Number(c.total_id_card_approved || 0);
      mpRows.push([idx + 1, r.rc_no, r.project_name, r.client_name, r.branch_name,
        c.total_employee_as_per_hrms ?? "", c.total_id_card_applied ?? "", c.total_id_card_approved ?? ""]);
    });
    mpRows.push(["", "", "", "", `Total · ${filtered.length} RCs`, sHrms, sApp, sAppr]);
    const ws2 = XLSX.utils.aoa_to_sheet(mpRows);
    XLSX.utils.book_append_sheet(wb, ws2, "Manpower & ID Cards");

    const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    XLSX.writeFile(wb, `HR_Compliance_FY${fy}_${ts}.xlsx`);
    toast.success(`Exported ${filtered.length} RC(s) · 2 sheets`);
  };

  return (
    <div data-testid="hr-compliance-page">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Compliance"
        title="HR Compliance Status"
        subtitle={`Standalone Rate Contract (RC) register — independent of the Projects master. Add RCs manually and track compliance (Applied vs Approved) month by month, Apr–Mar.${fy ? ` · FY ${fy}` : ""}`}
        actions={
          <>
            {canCreate && (
              <button onClick={() => openManual(null)} data-testid="hr-add-manual-rc-btn"
                className="inline-flex items-center gap-1.5 bg-[#F97316] hover:bg-[#EA6A0A] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors">
                <Plus size={13} /> Add RC
              </button>
            )}
            <div className="flex items-center gap-1">
              <select data-testid="hr-fy-select" value={fy} onChange={(e) => setFY(e.target.value)}
                className="border border-slate-300 px-2 py-1.5 text-xs bg-white focus:border-[#F97316] focus:outline-none min-w-[110px]">
                {opts.fy_list?.map((y) => <option key={y} value={y}>FY {y}</option>)}
              </select>
              <button onClick={addNewFY} title="Add a new Financial Year" data-testid="hr-fy-add"
                className="h-[30px] w-[30px] inline-flex items-center justify-center border border-slate-300 hover:border-[#F97316] hover:text-[#F97316] text-slate-600 transition-colors">
                <Plus size={14} />
              </button>
            </div>
            {canExport && (
              <button onClick={exportExcel} data-testid="hr-export-btn"
                className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-[#F97316] hover:text-[#F97316] text-slate-700 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors">
                <Download size={13} /> Export Excel
              </button>
            )}
          </>
        }
      />

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-slate-200" data-testid="hr-tabs">
        {[["monthly", "Monthly Compliance Status"], ["manpower", "Manpower & ID Card Summary"]].map(([k, l]) => (
          <button key={k} data-testid={`hr-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-xs uppercase tracking-wider font-bold border-b-2 -mb-px ${tab === k ? "border-orange-500 text-orange-600" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Filters */}
      <Card className="px-4 py-3 mb-3 flex items-center flex-wrap gap-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Filters</div>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2 text-slate-400" />
          <input data-testid="hr-search" placeholder="Search RC #, project, client…"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="border border-slate-300 pl-8 pr-2 py-1.5 text-xs w-64 focus:border-[#F97316] focus:outline-none" />
        </div>
        <BranchFilter value={branchFilter} onChange={setBranchFilter} testid="hr-filter-branch" />
        <select data-testid="hr-filter-project" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}
          className="border border-slate-300 px-2 py-1.5 text-xs bg-white focus:border-[#F97316] focus:outline-none min-w-[180px] max-w-[260px]">
          <option value="">All projects / sites</option>
          {projectOptions.map((name) => <option key={name} value={name}>{name}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 text-xs text-slate-700">
          <Switch checked={activeOnly} onCheckedChange={setActiveOnly} data-testid="hr-active-only" />
          <span>Active RCs only</span>
        </label>
        <div className="ml-auto flex items-center gap-3 text-[11px] text-slate-600" data-testid="hr-totals">
          <span><strong className="text-slate-900">{filtered.length}</strong> RCs</span>
          <span className="text-amber-700"><strong>{totals.applied}</strong> applied</span>
          <span className="text-emerald-700"><strong>{totals.approved}</strong> approved</span>
          {totals.noWork > 0 && <span className="text-rose-700"><strong>{totals.noWork}</strong> no-work</span>}
        </div>
      </Card>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <EmptyState icon={ClipboardList} title="No rate contracts found"
          subtitle={q || projectFilter || branchFilter || activeOnly
            ? "No RCs match the current filters."
            : "This register is independent of the Projects master. Click Add RC to manually add a Rate Contract and start tracking compliance."}
          testid="hr-empty" />
      )}

      {/* Monthly Compliance table */}
      {!loading && filtered.length > 0 && tab === "monthly" && (
        <>
          <Card data-testid="hr-table" className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" style={{ minWidth: "1100px" }}>
              <thead>
                <tr className="bg-slate-50 border-b-2 border-slate-200 text-slate-600">
                  <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "130px" }}>RC No</th>
                  <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "180px" }}>Project</th>
                  <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "130px" }}>Client</th>
                  <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "100px" }}>Branch</th>
                  <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "80px" }}>Status</th>
                  <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider">Monthly Compliance (FY {fy})</th>
                  <th className="text-right px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "70px" }}>C3/ID</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.key} data-testid={`hr-row-${r.rc_no}`} onClick={() => openDetail(r)}
                    className="border-b border-slate-100 hover:bg-orange-50/40 cursor-pointer align-top transition-colors">
                    <td className="px-3 py-3">
                      <div className="font-semibold text-slate-900 break-words">{r.rc_no || "—"}</div>
                      {r.is_manual && <span className="inline-block mt-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-violet-50 text-violet-700 border border-violet-200" data-testid={`hr-manual-badge-${r.rc_no}`}>Manual</span>}
                      {!r.is_manual && r.is_current && <span className="inline-block mt-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200">Current</span>}
                    </td>
                    <td className="px-3 py-3 text-slate-700 break-words">
                      <div>{r.project_name}</div>
                      <div className="text-[10px] text-slate-400">{r.is_manual ? (r.rc_description || "Manual RC") : r.project_code}</div>
                    </td>
                    <td className="px-3 py-3 text-slate-600 break-words">{r.client_name || "—"}</td>
                    <td className="px-3 py-3 text-slate-600 break-words">{r.branch_name || "—"}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold border ${r.project_status === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                        {r.project_status || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="grid grid-cols-6 gap-1">
                        {opts.months?.map((m) => {
                          const st = r.fy_months?.[m] || {};
                          return (
                            <div key={m} className={`border px-1 py-1 text-center ${cellTone(st)}`}>
                              <div className="text-[9px] font-semibold uppercase tracking-wider opacity-70">{m}</div>
                              <div className="text-[10px] font-bold mt-0.5">{cellLabel(st)}</div>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <button onClick={(e) => { e.stopPropagation(); openEdit(r); }}
                        className="text-slate-500 hover:text-[#F97316] p-1" title="Edit C3 / ID Cards / NOC details"
                        data-testid={`hr-edit-${r.rc_no}`}>
                        <Edit3 size={15} />
                      </button>
                      {r.is_manual && canEdit && (
                        <button onClick={(e) => { e.stopPropagation(); openManual(r); }}
                          className="text-slate-500 hover:text-violet-600 p-1" title="Edit manual RC details"
                          data-testid={`hr-manual-edit-${r.rc_no}`}>
                          <Pencil size={15} />
                        </button>
                      )}
                      {r.is_manual && canDelete && (
                        <button onClick={(e) => { e.stopPropagation(); deleteManual(r); }}
                          className="text-slate-500 hover:text-rose-600 p-1" title="Delete manual RC"
                          data-testid={`hr-manual-delete-${r.rc_no}`}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <p className="text-[11px] text-slate-500 mt-3">
            <span className="inline-block w-3 h-3 bg-amber-50 border border-amber-300 align-middle mr-1"></span> Applied
            <span className="inline-block w-3 h-3 bg-emerald-50 border border-emerald-300 align-middle ml-3 mr-1"></span> Approved
            <span className="inline-block w-3 h-3 bg-rose-50 border border-rose-300 align-middle ml-3 mr-1"></span> No Work
            <span className="inline-block w-3 h-3 bg-slate-50 border border-slate-200 align-middle ml-3 mr-1"></span> No status yet
            — click any row to update monthly status.
          </p>
        </>
      )}

      {/* Manpower & ID Card Summary */}
      {!loading && filtered.length > 0 && tab === "manpower" && (
        <Card data-testid="hr-manpower-table" className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" style={{ minWidth: "900px" }}>
            <thead>
              <tr className="bg-slate-50 border-b-2 border-slate-200 text-slate-600">
                <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "60px" }}>Sl No</th>
                <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "150px" }}>RC No</th>
                <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider">Project</th>
                <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "150px" }}>Client</th>
                <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "110px" }}>Branch</th>
                <th className="text-right px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "140px" }}>Employees per HRMS</th>
                <th className="text-right px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "130px" }}>ID Cards Applied</th>
                <th className="text-right px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ width: "140px" }}>ID Cards Approved</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, idx) => {
                const c = r.compliance || {};
                const shortfall = c.total_id_card_approved != null && c.total_id_card_applied != null
                  && c.total_id_card_approved < c.total_id_card_applied;
                return (
                  <tr key={r.key} data-testid={`hr-mp-row-${r.rc_no}`} onClick={() => openEdit(r)}
                    className="border-b border-slate-100 hover:bg-orange-50/40 cursor-pointer transition-colors">
                    <td className="px-3 py-2.5 text-slate-500 tabular">{String(idx + 1).padStart(2, "0")}</td>
                    <td className="px-3 py-2.5 font-semibold text-slate-900">{r.rc_no || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-700">{r.project_name}</td>
                    <td className="px-3 py-2.5 text-slate-600">{r.client_name || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-600">{r.branch_name || "—"}</td>
                    <td className="px-3 py-2.5 text-right tabular text-slate-900">{c.total_employee_as_per_hrms ?? "—"}</td>
                    <td className="px-3 py-2.5 text-right tabular text-slate-900">{c.total_id_card_applied ?? "—"}</td>
                    <td className={`px-3 py-2.5 text-right tabular font-semibold ${c.total_id_card_approved == null ? "text-slate-400" : shortfall ? "text-amber-700" : "text-emerald-700"}`}>
                      {c.total_id_card_approved ?? "—"}
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-slate-50 border-t-2 border-slate-200" data-testid="hr-mp-totals">
                <td colSpan={5} className="px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600">Total · {filtered.length} RCs</td>
                <td className="px-3 py-2.5 text-right tabular font-bold text-slate-900">
                  {filtered.reduce((s, r) => s + (Number(r.compliance?.total_employee_as_per_hrms) || 0), 0)}
                </td>
                <td className="px-3 py-2.5 text-right tabular font-bold text-slate-900">
                  {filtered.reduce((s, r) => s + (Number(r.compliance?.total_id_card_applied) || 0), 0)}
                </td>
                <td className="px-3 py-2.5 text-right tabular font-bold text-emerald-700">
                  {filtered.reduce((s, r) => s + (Number(r.compliance?.total_id_card_approved) || 0), 0)}
                </td>
              </tr>
            </tbody>
          </table>
        </Card>
      )}

      {/* Detail — Monthly grid dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-[1220px] rounded-none" data-testid="hr-detail-dialog">
          <DialogHeader>
            <DialogTitle>
              {detailRC?.rc_no || "RC"} · <span className="text-slate-500 font-normal">{detailRC?.project_name}</span>
              <span className="ml-3 text-xs font-normal text-orange-600">FY {fy}</span>
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Update the month-by-month compliance status for this Rate Contract. Each save is audit-logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-1 max-h-[72vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs bg-slate-50 border border-slate-200 px-3 py-2">
              <KV k="Client" v={detailRC?.client_name || "—"} />
              <KV k="Branch" v={detailRC?.branch_name || "—"} />
              <KV k="Project Status" v={detailRC?.project_status || "—"} />
              <KV k="RC Amount" v={formatINR(detailRC?.rc_amount || 0)} />
              <KV k="RC Date" v={detailRC?.rc_date || "—"} />
              <KV k="RC Validity" v={detailRC?.rc_validity_date || "—"} />
              <KV k="Employees in C3 / HRMS" v={`${detailRC?.compliance?.total_employee_in_c3 ?? "—"} / ${detailRC?.compliance?.total_employee_as_per_hrms ?? "—"}`} />
              <KV k="ID Cards Applied / Approved" v={`${detailRC?.compliance?.total_id_card_applied ?? "—"} / ${detailRC?.compliance?.total_id_card_approved ?? "—"}`} />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Monthly Compliance Status — FY {fy}</div>
              <div className="overflow-x-auto border border-slate-200">
                <table className="w-full text-sm" style={{ minWidth: "1120px" }}>
                  <thead className="bg-slate-50">
                    <tr className="border-b border-slate-200">
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600" style={{ width: "60px" }}>Month</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-rose-700 bg-rose-50/60" style={{ width: "70px" }}>No Work</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600" style={{ width: "70px" }}>Applied?</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600" style={{ width: "145px" }}>Applied Date</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600" style={{ width: "140px" }}>Compliance ID</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600" style={{ width: "70px" }}>Approved?</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600" style={{ width: "145px" }}>Approval Date</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600" style={{ width: "160px" }}>Remarks</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600" style={{ width: "55px" }}>Save</th>
                    </tr>
                  </thead>
                  <tbody>
                    {opts.months?.map((m) => {
                      const st = monthForm[m] || {};
                      const isNoWork = !!st.no_work;
                      return (
                        <tr key={m} className={`border-b border-slate-100 ${isNoWork ? "bg-rose-50/40" : ""}`} data-testid={`hr-detail-${m}`}>
                          <td className="px-3 py-2 font-semibold text-slate-900">{m}</td>
                          <td className="px-3 py-2 text-center bg-rose-50/30">
                            <Switch checked={isNoWork} disabled={!canEdit} data-testid={`hr-no-work-${m}`}
                              onCheckedChange={(v) => updateMonth(m, v
                                ? { no_work: true, compliance_applied: false, compliance_applied_date: "", compliance_id: "", approval_status: false, approval_date: "" }
                                : { no_work: false })}
                              className="data-[state=checked]:bg-rose-600" />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <Switch checked={!!st.compliance_applied} disabled={!canEdit || isNoWork}
                              data-testid={`hr-applied-${m}`}
                              onCheckedChange={(v) => updateMonth(m, v ? { compliance_applied: true } : { compliance_applied: false, compliance_applied_date: "", compliance_id: "", approval_status: false, approval_date: "" })} />
                          </td>
                          <td className="px-2 py-2">
                            <input type="date" value={st.compliance_applied_date || ""} disabled={!canEdit || !st.compliance_applied || isNoWork}
                              onChange={(e) => updateMonth(m, { compliance_applied_date: e.target.value })}
                              className={`${inputCls} h-9 text-xs w-full disabled:bg-slate-50 disabled:text-slate-400`} />
                          </td>
                          <td className="px-2 py-2">
                            <input value={st.compliance_id || ""} placeholder="CMP-…" disabled={!canEdit || !st.compliance_applied || isNoWork}
                              data-testid={`hr-compliance-id-${m}`}
                              onChange={(e) => updateMonth(m, { compliance_id: e.target.value })}
                              className={`${inputCls} h-9 text-xs w-full disabled:bg-slate-50 disabled:text-slate-400`} />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <Switch checked={!!st.approval_status} disabled={!canEdit || !st.compliance_applied || isNoWork}
                              data-testid={`hr-approved-${m}`}
                              onCheckedChange={(v) => updateMonth(m, v ? { approval_status: true } : { approval_status: false, approval_date: "" })} />
                          </td>
                          <td className="px-2 py-2">
                            <input type="date" value={st.approval_date || ""} disabled={!canEdit || !st.approval_status || isNoWork}
                              onChange={(e) => updateMonth(m, { approval_date: e.target.value })}
                              className={`${inputCls} h-9 text-xs w-full disabled:bg-slate-50 disabled:text-slate-400`} />
                          </td>
                          <td className="px-2 py-2">
                            <input value={st.remarks || ""} placeholder="Remarks (audit)" disabled={!canEdit}
                              data-testid={`hr-remarks-${m}`}
                              onChange={(e) => updateMonth(m, { remarks: e.target.value })}
                              className={`${inputCls} h-9 text-xs w-full disabled:bg-slate-50 disabled:text-slate-400`} />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Button onClick={() => saveMonth(m)} disabled={!canEdit || savingMonth === m} size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 rounded-none h-8 px-2 text-xs" data-testid={`hr-save-${m}`}>
                              {savingMonth === m ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Toggle <span className="font-semibold text-rose-700">No Work</span> when no manpower was deployed that month — compliance becomes not-applicable. Otherwise flow Applied → Approved. Every save is audit-logged.
              </p>
            </div>
            {history.length > 0 && (
              <div data-testid="hr-history">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                  <History size={12} /> Audit Trail (latest first)
                </div>
                <div className="border border-slate-200 divide-y divide-slate-100 max-h-40 overflow-y-auto">
                  {history.slice(0, 20).map((h, i) => (
                    <div key={i} className="px-3 py-1.5 text-[11px] flex items-start gap-2">
                      <span className="text-slate-400 whitespace-nowrap">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                      <span className="text-slate-700 flex-1">
                        {h.previous_status && h.new_status && (
                          <span className="mr-1.5 font-semibold">
                            <span className="text-slate-500">{h.previous_status}</span>
                            <span className="text-slate-400 mx-1">→</span>
                            <span className={h.new_status === "Approved" ? "text-emerald-700" : h.new_status === "No Work" ? "text-rose-700" : h.new_status === "Applied" ? "text-amber-700" : "text-slate-600"}>{h.new_status}</span>
                          </span>
                        )}
                        {h.note}
                        {h.remarks && <span className="italic text-slate-500"> — “{h.remarks}”</span>}
                        {Array.isArray(h.attachments) && h.attachments.length > 0 && (
                          <span className="text-slate-500"> · <Paperclip size={10} className="inline -mt-0.5" /> {h.attachments.join(", ")}</span>
                        )}
                      </span>
                      <span className="text-slate-400">{h.by}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)} className="rounded-none" data-testid="hr-detail-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* C3 / ID Cards / NOC edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-4xl rounded-none" data-testid="hr-edit-dialog">
          <DialogHeader>
            <DialogTitle>C3 / ID Cards / NOC — {editRC?.rc_no || "RC"} <span className="text-slate-500 font-normal">· {editRC?.project_name}</span></DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Manage the C3 form, ID card counts and NOC details for this Rate Contract.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto pr-2 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs bg-slate-50 border border-slate-200 px-3 py-2">
              <KV k={editRC?.is_manual ? "RC No (Manual)" : "RC No (from Project)"} v={editRC?.rc_no || "—"} />
              <KV k="RC Date" v={editRC?.rc_date || "—"} />
              <KV k="RC Validity" v={editRC?.rc_validity_date || "—"} />
              <KV k="RC Amount" v={formatINR(editRC?.rc_amount || 0)} />
            </div>
            <p className="text-[11px] text-slate-500 -mt-2">
              {editRC?.is_manual
                ? "This is a manually added RC — edit its number, dates and value via the pencil icon on the list."
                : "RC number, dates and value are managed on the Project master (Projects → Edit → Contracts) and are read-only here."}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="C3 Applied">
                <label className="inline-flex items-center gap-2 mt-2">
                  <Switch checked={blockForm.c3_applied} disabled={!canEdit} data-testid="hr-form-c3-applied"
                    onCheckedChange={(v) => setBlockForm({ ...blockForm, c3_applied: v })} />
                  <span className="text-sm">{blockForm.c3_applied ? "Yes" : "No"}</span>
                </label>
              </Field>
              <Field label="C3 Applied Date">
                <input type="date" value={blockForm.c3_applied_date || ""} disabled={!canEdit || !blockForm.c3_applied}
                  onChange={(e) => setBlockForm({ ...blockForm, c3_applied_date: e.target.value })}
                  className={`${inputCls} disabled:bg-slate-50 disabled:text-slate-400`} />
              </Field>
              <Field label="C3 Received Date">
                <input type="date" value={blockForm.c3_received_date || ""} disabled={!canEdit}
                  onChange={(e) => setBlockForm({ ...blockForm, c3_received_date: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Employees in C3">
                <input type="number" min="0" value={blockForm.total_employee_in_c3} disabled={!canEdit}
                  data-testid="hr-form-emp-c3"
                  onChange={(e) => setBlockForm({ ...blockForm, total_employee_in_c3: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Employees per HRMS">
                <input type="number" min="0" value={blockForm.total_employee_as_per_hrms} disabled={!canEdit}
                  data-testid="hr-form-emp-hrms"
                  onChange={(e) => setBlockForm({ ...blockForm, total_employee_as_per_hrms: e.target.value })} className={inputCls} />
              </Field>
              <Field label="ID Cards Applied">
                <input type="number" min="0" value={blockForm.total_id_card_applied} disabled={!canEdit}
                  data-testid="hr-form-id-applied"
                  onChange={(e) => setBlockForm({ ...blockForm, total_id_card_applied: e.target.value })} className={inputCls} />
              </Field>
              <Field label="ID Card Applied — Status Update Date">
                <input type="date" value={blockForm.id_card_applied_status_update_date || ""} disabled={!canEdit}
                  onChange={(e) => setBlockForm({ ...blockForm, id_card_applied_status_update_date: e.target.value })} className={inputCls} />
              </Field>
              <Field label="ID Cards Approved">
                <input type="number" min="0" value={blockForm.total_id_card_approved} disabled={!canEdit}
                  data-testid="hr-form-id-approved"
                  onChange={(e) => setBlockForm({ ...blockForm, total_id_card_approved: e.target.value })} className={inputCls} />
              </Field>
              <Field label="ID Card Approved — Status Update Date">
                <input type="date" value={blockForm.id_card_approved_status_update_date || ""} disabled={!canEdit}
                  onChange={(e) => setBlockForm({ ...blockForm, id_card_approved_status_update_date: e.target.value })} className={inputCls} />
              </Field>
              <Field label="NOC Received">
                <label className="inline-flex items-center gap-2 mt-2">
                  <Switch checked={blockForm.noc_received} disabled={!canEdit} data-testid="hr-form-noc"
                    onCheckedChange={(v) => setBlockForm({ ...blockForm, noc_received: v })} />
                  <span className="text-sm">{blockForm.noc_received ? "Yes" : "No"}</span>
                </label>
              </Field>
              <Field label="NOC Received Date">
                <input type="date" value={blockForm.noc_received_date || ""} disabled={!canEdit || !blockForm.noc_received}
                  onChange={(e) => setBlockForm({ ...blockForm, noc_received_date: e.target.value })}
                  className={`${inputCls} disabled:bg-slate-50 disabled:text-slate-400`} />
              </Field>
            </div>
            <div className="pt-2 border-t border-slate-200 space-y-3">
              <Field label="Remarks (recorded in the audit trail)" full>
                <input value={blockForm.remarks || ""} disabled={!canEdit} placeholder="Reason / context for this update…"
                  data-testid="hr-form-remarks"
                  onChange={(e) => setBlockForm({ ...blockForm, remarks: e.target.value })} className={inputCls} />
              </Field>
              <AttachmentsField label="Compliance Documents" value={blockForm.attachments || []} disabled={!canEdit}
                onChange={(v) => setBlockForm({ ...blockForm, attachments: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-none" data-testid="hr-form-cancel">
              <X size={14} className="mr-1" /> Cancel
            </Button>
            {canEdit && (
              <Button onClick={saveBlock} disabled={savingBlock} className="bg-[#F97316] hover:bg-[#EA6A0A] rounded-none" data-testid="hr-form-save">
                {savingBlock ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />} Save
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Manual RC add/edit dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-2xl rounded-none" data-testid="hr-manual-dialog">
          <DialogHeader>
            <DialogTitle>{manualId ? `Edit Manual RC — ${manualForm.rc_no}` : "Add Manual RC"}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Standalone Registration Certificate — tracked here for HR compliance without linking to a PrimeWork project. Reflected on the Debtor/HR Management Dashboard.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="RC Number *">
                <input value={manualForm.rc_no} data-testid="hr-manual-rc-no" placeholder="e.g. RC/2026/0042"
                  onChange={(e) => setManualForm({ ...manualForm, rc_no: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Project / Site Name *">
                <input value={manualForm.project_name} data-testid="hr-manual-project-name" placeholder="Site or establishment name"
                  onChange={(e) => setManualForm({ ...manualForm, project_name: e.target.value })} className={inputCls} />
              </Field>
              <Field label="RC Name / Description" full>
                <input value={manualForm.rc_description} data-testid="hr-manual-description" placeholder="Nature of work / licence description"
                  onChange={(e) => setManualForm({ ...manualForm, rc_description: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Client Name">
                <input value={manualForm.client_name} data-testid="hr-manual-client"
                  onChange={(e) => setManualForm({ ...manualForm, client_name: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Branch">
                <select value={manualForm.branch_id} data-testid="hr-manual-branch"
                  onChange={(e) => setManualForm({ ...manualForm, branch_id: e.target.value })}
                  className={`${inputCls} bg-white`}>
                  <option value="">No branch (visible to all)</option>
                  {(branches || []).map((b) => <option key={b.id} value={b.id}>{b.name || b.branch_name}</option>)}
                </select>
              </Field>
              <Field label="RC Date (Valid From)">
                <input type="date" value={manualForm.rc_date || ""} data-testid="hr-manual-rc-date"
                  onChange={(e) => setManualForm({ ...manualForm, rc_date: e.target.value })} className={inputCls} />
              </Field>
              <Field label="RC Validity (Valid To)">
                <input type="date" value={manualForm.rc_validity_date || ""} data-testid="hr-manual-validity"
                  onChange={(e) => setManualForm({ ...manualForm, rc_validity_date: e.target.value })} className={inputCls} />
              </Field>
              <Field label="RC Amount (₹)">
                <input type="number" min="0" value={manualForm.rc_amount} data-testid="hr-manual-amount"
                  onChange={(e) => setManualForm({ ...manualForm, rc_amount: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Active">
                <label className="inline-flex items-center gap-2 mt-2">
                  <Switch checked={manualForm.active} data-testid="hr-manual-active"
                    onCheckedChange={(v) => setManualForm({ ...manualForm, active: v })} />
                  <span className="text-sm">{manualForm.active ? "Active" : "Inactive"}</span>
                </label>
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)} className="rounded-none" data-testid="hr-manual-cancel">
              <X size={14} className="mr-1" /> Cancel
            </Button>
            <Button onClick={saveManual} disabled={savingManual} className="bg-[#F97316] hover:bg-[#EA6A0A] rounded-none" data-testid="hr-manual-save">
              {savingManual ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
              {manualId ? "Update RC" : "Add RC"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const KV = ({ k, v }) => (
  <div>
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{k}</div>
    <div className="font-medium text-slate-800">{v}</div>
  </div>
);
